import { useEffect, useState } from "react";
import { CheckCircle2, Flag, Gavel, Loader2, XCircle } from "lucide-react";
import toast from "react-hot-toast";
import { Button } from "@/components/ui/Button";
import { PageLoader } from "@/components/brand/PageLoader";
import { supabase } from "@/lib/supabase";

type ReportStatus = "open" | "dismissed" | "actioned";
type ReasonCode = "abuse" | "cheating" | "unsafe" | "no_show" | "other";

interface ReportRow {
  id: string;
  reporter_id: string;
  reported_user_id: string;
  reason: ReasonCode;
  notes: string | null;
  status: ReportStatus;
  created_at: string;
  reporter_name: string | null;
  reported_name: string | null;
  reported_flag_count: number;
}

const REASON_LABEL: Record<ReasonCode, string> = {
  abuse: "Abuse",
  cheating: "Cheating",
  unsafe: "Unsafe conduct",
  no_show: "No-show",
  other: "Other",
};

/**
 * Admin → Flags. Shows every player report (reporter → reported user)
 * with status, reason, free-text notes, and the reported user's
 * current flag count. Dismiss clears the open flag and decrements the
 * user's count; Action marks the report as handled (used when a
 * manual ban follows). Every action writes an audit_log entry via the
 * log_audit RPC.
 */
export function AdminFlags() {
  const [rows, setRows] = useState<ReportRow[] | null>(null);
  const [status, setStatus] = useState<ReportStatus | "all">("open");
  const [busyId, setBusyId] = useState<string | null>(null);

  async function load() {
    let q = supabase
      .from("player_reports")
      .select(
        "id, reporter_id, reported_user_id, reason, notes, status, created_at, reporter:profiles!player_reports_reporter_profile_fkey(display_name), reported:profiles!player_reports_reported_profile_fkey(display_name, flag_count)"
      )
      .order("created_at", { ascending: false })
      .limit(200);
    if (status !== "all") q = q.eq("status", status);
    const { data, error } = await q;
    if (error) {
      toast.error(error.message);
      setRows([]);
      return;
    }
    // Flatten the nested joins.
    setRows(
      ((data ?? []) as unknown as Array<{
        id: string;
        reporter_id: string;
        reported_user_id: string;
        reason: ReasonCode;
        notes: string | null;
        status: ReportStatus;
        created_at: string;
        reporter: { display_name: string | null } | null;
        reported: { display_name: string | null; flag_count: number } | null;
      }>).map((r) => ({
        id: r.id,
        reporter_id: r.reporter_id,
        reported_user_id: r.reported_user_id,
        reason: r.reason,
        notes: r.notes,
        status: r.status,
        created_at: r.created_at,
        reporter_name: r.reporter?.display_name ?? null,
        reported_name: r.reported?.display_name ?? null,
        reported_flag_count: r.reported?.flag_count ?? 0,
      }))
    );
  }

  useEffect(() => {
    setRows(null);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  async function resolve(id: string, newStatus: "dismissed" | "actioned") {
    setBusyId(id);
    const { error } = await supabase
      .from("player_reports")
      .update({ status: newStatus, resolved_at: new Date().toISOString() })
      .eq("id", id);
    if (error) {
      setBusyId(null);
      return toast.error(error.message);
    }
    await supabase.rpc("log_audit", {
      action: `player_report_${newStatus}`,
      entity_type: "player_report",
      entity_id: id,
      details: {},
    });
    toast.success(newStatus === "dismissed" ? "Dismissed" : "Marked as actioned");
    setBusyId(null);
    load();
  }

  return (
    <div>
      <header className="mb-6">
        <p className="mb-1.5 text-xs font-bold uppercase tracking-wider text-volt">
          Admin · Flags
        </p>
        <h1 className="text-3xl font-semibold tracking-tight">Flags</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Every player report raised from the app. Dismissing an open
          report decrements the reported user's flag count; Actioned
          keeps it (use when a ban follows separately).
        </p>
      </header>

      <StatusTabs value={status} onChange={setStatus} />

      {rows === null ? (
        <PageLoader />
      ) : rows.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-border bg-card">
          <table className="w-full min-w-[640px] text-sm">
            <thead className="border-b border-border bg-secondary/40 text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-4 py-2 text-left">Reported</th>
                <th className="px-4 py-2 text-left">Reason</th>
                <th className="px-4 py-2 text-left">Reporter</th>
                <th className="px-4 py-2 text-left">Notes</th>
                <th className="px-4 py-2 text-left">When</th>
                <th className="px-4 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-border last:border-b-0 align-top">
                  <td className="px-4 py-3">
                    <div className="font-medium">
                      {r.reported_name ?? <span className="text-muted-foreground">Unknown</span>}
                    </div>
                    <div className="mt-0.5 text-[10px] text-muted-foreground">
                      {r.reported_flag_count} total flag{r.reported_flag_count === 1 ? "" : "s"}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="rounded-full bg-volt-subtle px-2 py-0.5 text-[10px] font-bold text-volt">
                      {REASON_LABEL[r.reason]}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs">
                    {r.reporter_name ?? <span className="text-muted-foreground">Unknown</span>}
                  </td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">
                    {r.notes ? (
                      <span className="line-clamp-3">{r.notes}</span>
                    ) : (
                      <span className="opacity-60">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">
                    {new Date(r.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3">
                    {r.status !== "open" ? (
                      <StatusPill status={r.status} />
                    ) : (
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => resolve(r.id, "dismissed")}
                          disabled={busyId === r.id}
                        >
                          {busyId === r.id ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <XCircle className="h-3 w-3" />
                          )}
                          Dismiss
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => resolve(r.id, "actioned")}
                          disabled={busyId === r.id}
                        >
                          <Gavel className="h-3 w-3" />
                          Action
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function StatusTabs({
  value,
  onChange,
}: {
  value: ReportStatus | "all";
  onChange: (v: ReportStatus | "all") => void;
}) {
  const options: Array<{ label: string; value: ReportStatus | "all" }> = [
    { label: "Open", value: "open" },
    { label: "Dismissed", value: "dismissed" },
    { label: "Actioned", value: "actioned" },
    { label: "All", value: "all" },
  ];
  return (
    <div className="mb-4 flex flex-wrap gap-2">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          className={
            "rounded-full px-3 py-1 text-xs font-medium transition-colors " +
            (value === o.value
              ? "bg-volt text-primary-foreground"
              : "bg-secondary text-muted-foreground hover:bg-volt-subtle hover:text-foreground")
          }
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function StatusPill({ status }: { status: ReportStatus }) {
  if (status === "dismissed") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-secondary px-2 py-0.5 text-[10px] font-bold text-muted-foreground">
        <XCircle className="h-2.5 w-2.5" /> Dismissed
      </span>
    );
  }
  if (status === "actioned") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-volt/15 px-2 py-0.5 text-[10px] font-bold text-volt">
        <CheckCircle2 className="h-2.5 w-2.5" /> Actioned
      </span>
    );
  }
  return null;
}

function EmptyState() {
  return (
    <div className="rounded-2xl border border-dashed border-border bg-card/40 p-10 text-center">
      <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-volt-subtle text-volt">
        <Flag className="h-5 w-5" />
      </div>
      <p className="text-sm font-semibold">No flags</p>
      <p className="mt-1 text-xs text-muted-foreground">
        When players report each other from the mobile app, entries
        show up here.
      </p>
    </div>
  );
}
